import { z } from 'zod';

import {
    LINE_STATE_VALUES,
    RETURN_ORDER_TYPE_VALUES,
    RETURN_REASON_VALUES,
    RETURN_RESOLUTION_VALUES,
    RETURN_STATUS_VALUES,
} from '../fieldEnums';
import { doc, documentedStringEnum, itemCountField, optionalDocumentedStringEnum } from './openapiHelpers';

const fileSchema = z.object({
    id: doc(z.number().int().optional(), 'File ID when updating an existing attachment.'),
    data: doc(z.string().optional(), 'Base64 content. Required for new files.'),
    fileName: doc(z.string(), 'Original file name.'),
    type: doc(z.string().min(1), 'Attachment category (free-form string, same as v2).'),
    fileType: doc(z.string(), 'MIME type or extension.'),
    fileSize: doc(z.string().optional(), 'File size.'),
    createdAt: doc(z.union([z.string(), z.date()]).nullish(), 'Upload timestamp from files.CREATED_AT.'),
    creatorRole: doc(
        z.enum(['skladon_employee', 'client']).nullish(),
        'Who uploaded the file (Skladon employee or client).',
    ),
}).strict();

const returnProductRequestSchema = z.object({
    sku: doc(z.string().min(1), 'Returned product SKU.'),
    quantity: itemCountField(),
    returnReason: documentedStringEnum(RETURN_REASON_VALUES, 'Reason code for the return line.'),
    lineState: documentedStringEnum(
        LINE_STATE_VALUES,
        'Line state. E = enabled, D = disabled. If omitted on create, defaults to E.',
    ).optional(),
}).strict();

const returnProductResponseSchema = returnProductRequestSchema.extend({
    resolution: optionalDocumentedStringEnum(
        RETURN_RESOLUTION_VALUES,
        'Resolution applied to the line (read-only). Not accepted on write in v2.1.',
    ),
    received: doc(z.number().int().nullish(), 'Quantity received back in the warehouse for this line.'),
    serialNumber: doc(z.string().nullish(), 'Serial number of the returned item, when tracked.'),
});

const returnV21RequestBodyFields = {
    note: doc(z.string().nullish(), 'Customer-visible return note.'),
    eshopNote: doc(z.string().nullish(), 'Your internal note stored on the return.'),
    description: doc(z.string().nullish(), 'Return description.'),
    shipper: doc(z.string().nullish(), 'Free-text return carrier. Not a code from GET /shipper-service-codes.'),
    trackingNumber: doc(z.string().nullish(), 'Return shipment tracking number.'),
    shipperServiceCode: doc(z.string().nullish(), 'Unused on returns. Ignored — do not send a shipper-service-codes value.'),
    orderType: documentedStringEnum(
        RETURN_ORDER_TYPE_VALUES,
        'Order type linked to the return.',
    ).optional(),
    returnedOrderProducts: doc(z.array(returnProductRequestSchema).optional(), 'Products being returned.'),
    files: doc(z.array(fileSchema).optional(), 'Files attached to the return.'),
} as const;

export const createReturnV21Schema = z.object({
    eshopReturnId: doc(z.string().min(1), 'Your return (RMA) identifier — unique within the eshop.'),
    eshopOrderId: doc(z.string().nullish(), 'Related order identifier, when known.'),
    ...returnV21RequestBodyFields,
}).strict();

export const updateReturnV21Schema = z.object({
    ...returnV21RequestBodyFields,
}).strict();

export const patchReturnV21Schema = z.object({
    ...returnV21RequestBodyFields,
}).partial().strict().refine(
    (data) => Object.keys(data).length > 0,
    { message: 'At least one field must be provided' },
);

const returnV21ResponseReadOnly = {
    id: doc(z.number().int().optional(), 'Internal Skladon return ID.'),
    rma: doc(z.string(), 'Skladon RMA number assigned to the return.'),
    status: optionalDocumentedStringEnum(RETURN_STATUS_VALUES, 'Return workflow status (read-only).'),
    createdAt: doc(z.string(), 'Return creation timestamp.'),
    canceledAt: doc(z.string().nullish(), 'When the return was canceled.'),
    confirmedAt: doc(z.string().nullish(), 'When the return was confirmed.'),
    receivedAt: doc(z.string().nullish(), 'When goods were received back in the warehouse.'),
    sourceSys: doc(z.string().nullish(), 'System that created the return (e.g. wmsSkladon).'),
    transportPaidBy: doc(z.string().nullish(), 'Who pays for return transport.'),
    orderId: doc(z.number().int().nullish(), 'Internal Skladon order ID linked to the return.'),
    partnerOrderId: doc(z.string().nullish(), 'Partner / marketplace order reference.'),
    recipientName: doc(z.string().nullish(), 'Recipient name on the related order.'),
    senderStreet: doc(z.string().nullish(), 'Sender street for the return shipment.'),
    senderCity: doc(z.string().nullish(), 'Sender city for the return shipment.'),
    senderState: doc(z.string().nullish(), 'Sender state/region for the return shipment.'),
    senderPostcode: doc(z.string().nullish(), 'Sender postal code for the return shipment.'),
    senderPhone: doc(z.string().nullish(), 'Sender phone for the return shipment.'),
    orderShipper: doc(z.string().nullish(), 'Shipper of the original outbound order.'),
    orderShipperServiceCode: doc(z.string().nullish(), 'Service code of the original outbound order.'),
    orderTrackingNumber: doc(z.string().nullish(), 'Tracking number of the original outbound order.'),
} as const;

export const returnV21ResponseSchema = z.object({
    eshopReturnId: doc(z.string(), 'Your return identifier.'),
    eshopOrderId: doc(z.string().nullish(), 'Related order identifier.'),
    ...returnV21RequestBodyFields,
    returnedOrderProducts: z.array(returnProductResponseSchema).optional(),
    ...returnV21ResponseReadOnly,
}).strict();

export type CreateReturnV21 = z.infer<typeof createReturnV21Schema>;
export type UpdateReturnV21 = z.infer<typeof updateReturnV21Schema>;
export type PatchReturnV21 = z.infer<typeof patchReturnV21Schema>;
export type ReturnV21 = z.infer<typeof returnV21ResponseSchema>;
